import { TextLinkCaret } from "@vds/buttons";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { List } from "react-window";
import DashboardEventCard from "../../global/components/events/DashboardEventCard";
import DashboardEventRow from "../../global/components/events/DashboardEventRow";
import DashboardEventRowEmpty from "../../global/components/events/DashboardEventRowEmpty.jsx";
import { getImageObj, getIncidentsData } from "../../global/utility/Utils.js";
import { checkForEvidenceStatusFromDashbaord } from "../../global/utility/incidentsUtils.js";
import { fetchCriticalUnresolvedEventsRequest, loadingEvents} from "../../store/actions/index.jsx";
import { ContentContainer, TextLinkCaretButton, NewEventLink, TextLink } from "./styles";
import { APPLICATION_IDS_AND_DESC, DASHBOARD_EVENT_CARD_TEXT } from "./dashboardConstants.jsx";
import { getCriticalUnresolvedEvents } from "../../model/eventService.js";

// Defined outside EbcLeftContainer so its reference is stable — prevents react-window from remounting rows on every render
const EventRowRenderer = ({ index, style, ariaAttributes, eventList, expandedTileIndex, availableVideoEvidence, setAvailableVideoEvidence, handleTileExpand, setInitialSiteTag, setInitialFloorTag, onHoverChange }) => {
  const event = eventList[index];
  if (!event) return null;
  return (
    <div style={style} {...ariaAttributes}>
      <DashboardEventRow
        name={event?.title ? event?.title : event?.applicationId}
        summary={
          event?.applicationId == APPLICATION_IDS_AND_DESC.INTRUSION_DETECTION.ID
            ? APPLICATION_IDS_AND_DESC.INTRUSION_DETECTION.DESC
            : event?.applicationId == APPLICATION_IDS_AND_DESC.SLIP_AND_FALL.ID
              ? APPLICATION_IDS_AND_DESC.SLIP_AND_FALL.DESC
              : event?.applicationId == APPLICATION_IDS_AND_DESC.PHONE_DETECTION.ID
                ? APPLICATION_IDS_AND_DESC.PHONE_DETECTION.DESC
                : event?.description
        }
        location={event?.deviceLoc}
        eventTime={event.createdAt}
        video={event?.evidence?.thumbnail}
        isExpandedValue={event.id === expandedTileIndex}
        onExpand={(event, isExpanded) => handleTileExpand(event.id, isExpanded)}
        videoEvidence={getImageObj(event?.evidences ?? [], "video")}
        availableVideoEvidence={availableVideoEvidence}
        setAvailableVideoEvidence={setAvailableVideoEvidence}
        event={event}
        eventPriority={event?.priority}
        eventType={event?.type}
        imageEvidence={getImageObj(event?.evidences ?? [], "image")}
        evidenceStatus={event?.evidenceStatus ?? null}
        setInitialSiteTag={setInitialSiteTag}
        setInitialFloorTag={setInitialFloorTag}
        onHoverChange={onHoverChange}
      />
    </div>
  );
};

const EbcLeftContainer = ({ setInitialSiteTag, initialSiteTag, setInitialFloorTag, initialFloorTag, sitesList, userSelectedSiteRef }) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const [availableVideoEvidence, setAvailableVideoEvidence] = useState([]);
  const eventError = useSelector((state) => state.eventReducer.eventError);
  const [eventList, setEventList] = useState([]);
  const eventsLoading = useSelector((state) => state.eventReducer.loadingEvents);
  const criticalUnresolvedE = useSelector((state) => state.eventReducer.criticalUnresolved);
  const pageSize = useSelector((state) => state.eventReducer.pageSize);
  const criticalCount = useSelector((state) => state.eventReducer.criticalEventsCount);
  const hasFetchedCriticalEventsOnce = useSelector((state) => state.eventReducer.hasFetchedCriticalEventsOnce);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const [fetchCriticalUnresolvedEventInvoked, setFetchCriticalUnresolvedEventInvoked] = useState(false);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const [incidentPageNumber, setIncidentPageNumber] = useState(2);
  const [newEventDetected, setNewEventDetected] = useState(false);
  const [showNewEventBanner, setShowNewEventBanner] = useState(false);// We show New Event badge only when there is a new event and user has scrolled down, if user is already at top we directly show the new event without badge. This state is to track that scenario.
  const isScrolledDownRef = useRef(false); // Ref mirror of showNewEventBanner — avoids stale closure in criticalEventsSnapshot useEffect
  // True while any event row is hovered. Treated the same as isScrolledDownRef — prevents
  // new events from prepending to the list or switching the floor map while the user is
  // actively engaged with an event row (hover-freeze behaviour).
  const isHoveringRef = useRef(false);
  const [pageLimitReached, setPageLimitReached] = useState(false); // To track if user has reached end of pagination and show a message accordingly
  // Memoize only the properties that matter for tracking changes
  const criticalEventsSnapshot = useMemo(() => {
    return (
      criticalUnresolvedE?.map((event) => ({
        id: event.id,
        status: event.status,
        evidenceStatus: event.evidenceStatus,
        updatedAt: event.updatedAt,
        evidencesCount: event.evidences?.length || 0,
        hasVideoEvidence: event.evidences?.some((e) => e.hasMedia && e.mediaType === "video") || false,
        siteTag: event.deviceLoc?.site?.tag,
        floorTag: event.deviceLoc?.floor?.tag
      })) || []
    );
  }, [criticalUnresolvedE]);

  // Fallback: if siteTagList loads after the no-events response already ran
  // (and couldn't set defaults because siteTagList was empty at that time), set them now.
  // eventsLoading guard prevents premature execution before the fetch completes.
  useEffect(() => {
    if (siteTagList?.sites?.length > 0 && !eventsLoading && criticalUnresolvedE?.length === 0 && (!initialSiteTag || !initialFloorTag)) {
      const siteId = initialSiteTag || siteTagList.sites[0]?.id;
      if (!initialSiteTag) setInitialSiteTag(siteId);
      if (!initialFloorTag) {
        const firstFloor = siteTagList.floors?.find((floor) => floor.tagids?.includes(siteId));
        if (firstFloor?.id) setInitialFloorTag(firstFloor.id);
      }
    }
  }, [siteTagList, eventsLoading]);

  useEffect(() => {
    if(!activeSession?.accountId) {
      dispatch(loadingEvents(true));
      return;
    };

    setFetchCriticalUnresolvedEventInvoked(true);
    if (sitesList && sitesList.length) {
      dispatch(fetchCriticalUnresolvedEventsRequest(sitesList));
    } else {
      dispatch(fetchCriticalUnresolvedEventsRequest([]));
    }
  }, [sitesList?.length, activeSession?.accountId]);

  useEffect(() => {
    if (criticalUnresolvedE && criticalUnresolvedE.length > 0) {
      const incoming = criticalUnresolvedE || [];
      const existing = eventList || [];
      const incomingMap = new Map(incoming.filter(e => e?.id).map((e) => [e.id, e]));
      const existingIds = new Set(existing.filter(e => e?.id).map(e => e.id));
      const newEvents = incoming.filter((e) => e?.id && !existingIds.has(e.id));
      const updatedExisting = existing.filter(ev => ev?.id).map((ev) => incomingMap.get(ev.id) || ev);

      const userEngaged = isScrolledDownRef.current || isHoveringRef.current;
      // Also hold off auto-updates if user manually picked a site — they must click the
      // "new event detected" banner to pull in the new event and switch the floor map.
      // Once the banner is showing, keep it sticky (same as the scrolled-down case) so a
      // poll landing during a brief hover-off gap can't silently merge the withheld events
      // while leaving the banner stuck on — only scroll-to-top or the banner click clears it.
      const blockAutoUpdate = userEngaged || userSelectedSiteRef?.current || newEventDetected;
      if (newEvents.length > 0 && blockAutoUpdate) {
        // User is scrolled/hovering/on a manually-picked site — notify but don't disrupt.
        setNewEventDetected(true);
        setShowNewEventBanner(true);
      } else {
        // User is at top, not hovering, and hasn't manually picked a site — update list.
        const mergedList = [...newEvents, ...updatedExisting];
        setEventList(mergedList);
        if (!blockAutoUpdate) {
          // Switch to the top of the merged list (not the raw incoming array) so the
          // floor map always matches what's actually shown at the top of the event list.
          setInitialSiteTag(mergedList[0]?.deviceLoc?.site?.tag);
          setInitialFloorTag(mergedList[0]?.deviceLoc?.floor?.tag);
        }
      }
      checkForEvidenceStatusFromDashbaord(criticalUnresolvedE, timerRef, () => {
        const shouldUpdateSilently = true;
        setFetchCriticalUnresolvedEventInvoked(true);
        if (sitesList && sitesList.length) {
          dispatch(fetchCriticalUnresolvedEventsRequest(sitesList, shouldUpdateSilently));
        } else {
          dispatch(fetchCriticalUnresolvedEventsRequest([], shouldUpdateSilently));
        }
      });
    } else if(fetchCriticalUnresolvedEventInvoked && criticalUnresolvedE?.length === 0){
      setFetchCriticalUnresolvedEventInvoked(false);
      if (eventList.length > 0) {
        setEventList([]);
      }
    }

    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [criticalEventsSnapshot]);

  const navigate = useNavigate();
  const handleAllEventsClick = () => {
    navigate("/events", { state: { selectedCritical: "All" } });
  };
  const [expandedTileIndex, setExpandedTileIndex] = useState("");
  const handleTileExpand = (EventID, isExpanded) => {
      setExpandedTileIndex(isExpanded ? EventID : null);
  };
  const listRef = useRef(null);
  const isFetchingIncidentsRef = useRef(false);
  const cooldownRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const pageLimitTimeoutRef = useRef(null);

  const COLLAPSED_ROW_HEIGHT = 131; // 116px tile + 16px margin-bottom
  const EXPANDED_ROW_HEIGHT = 432;  // 416 container + 16 px margin-botom
  const LIST_HEIGHT = 624;          // matches max-height: 39em
  const MOBILE_VISIBLE_ROWS = 3;

  const getRowHeight = useCallback(
    (index) => {
      const event = eventList[index];
      return event?.id === expandedTileIndex ? EXPANDED_ROW_HEIGHT : COLLAPSED_ROW_HEIGHT;
    },
    [eventList, expandedTileIndex]
  );

  const handleScroll = async (e) => {
    const el = e.target;
    const threshold = 0; // px from bottom
    const topThreshold = 100; // px from top
    if (!el) return;
    // If user scrolls to top and a new event banner is visible, refresh list and hide banner.
    // Only treat this as a genuine "user scrolled back to top" action if they had actually
    // scrolled away first (isScrolledDownRef was true) — this branch can also fire from
    // non-user scroll events (e.g. list height recalculated after a row is added) while
    // already resting at the top, which must be ignored (no-op).
    if (el.scrollTop <= topThreshold) {
      const wasScrolledDown = isScrolledDownRef.current;
      if (wasScrolledDown && newEventDetected) {
        const existingIds = new Set((eventList || []).filter((e) => e?.id).map((e) => e.id));
        const newEvents = (criticalUnresolvedE || []).filter((e) => e?.id && !existingIds.has(e.id));
        const updatedList = [...newEvents, ...(eventList || [])];
        setEventList(updatedList);
        setNewEventDetected(false);
        setShowNewEventBanner(false);
        // Deliberate scroll-to-top is an explicit user action — switch floor to the newest
        // (top) event and drop the manual-selection block so future events auto-follow.
        // Use the merged list's top item (not the raw incoming array) so the floor map
        // always matches what's actually shown at the top of the event list.
        setInitialSiteTag(updatedList[0]?.deviceLoc?.site?.tag);
        setInitialFloorTag(updatedList[0]?.deviceLoc?.floor?.tag);
        if (userSelectedSiteRef) userSelectedSiteRef.current = false;
      }
      isScrolledDownRef.current = false;
      return;
    } else {
      // If user is not at top and a new event is detected, show banner
      setShowNewEventBanner(true);
      isScrolledDownRef.current = true;
    }
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      if(incidentPageNumber > pageSize){
        setPageLimitReached(true);
        if (pageLimitTimeoutRef.current) clearTimeout(pageLimitTimeoutRef.current);
        pageLimitTimeoutRef.current = setTimeout(() => {
          setPageLimitReached(false);
          pageLimitTimeoutRef.current = null;
        }, 3000);
        return;
      };
      if (isFetchingIncidentsRef.current) return;
      if (cooldownRef.current) return;
      if (!activeSession?.accountId) return;
      try {
        isFetchingIncidentsRef.current = true;
        const resp = await getCriticalUnresolvedEvents(activeSession, true, sitesList, incidentPageNumber);
        // increment page number after a successful API response
        const newEvents = resp?.res || [];
        if (newEvents && newEvents.length > 0) {
          setIncidentPageNumber((prev) => prev + 1);
          setEventList((prev) => {
            const existingIds = new Set((prev || []).map((e) => e?.id));
            const filtered = newEvents.filter((e) => e && !existingIds.has(e.id));
            return [...(prev || []), ...filtered];
          })
        } else {
          // No more events available, stop pagination
          isFetchingIncidentsRef.current = true; // Prevent further fetches
          return;
        }
      } catch (err) {
        console.error("getIncidentsData failed on scroll:", err);
      } finally {
        isFetchingIncidentsRef.current = false;
        cooldownRef.current = true;
        // start cooldown timer (500  milli-seconds)
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          cooldownRef.current = false;
          debounceTimerRef.current = null;
        }, 500);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (pageLimitTimeoutRef.current) {
        clearTimeout(pageLimitTimeoutRef.current);
        pageLimitTimeoutRef.current = null;
      }
    };
  }, []);

  const handleNewEventDetected = () => {
    // Capture before resetting — used below to decide whether to defer the scroll
    const wasExpanded = !!expandedTileIndex;
    // Collapse any open accordion before prepending — new events shift all indices,
    // causing react-window to miscalculate row heights while scrolling to top.
    setExpandedTileIndex(null);
    const newEvents = criticalUnresolvedE || [];
    if (newEvents && newEvents.length) {
      setEventList((prev) => {
        const existingIds = new Set((prev || []).map((e) => e?.id));
        const filtered = newEvents.filter((e) => e && !existingIds.has(e.id));
        return [...filtered, ...(prev || [])];
      });
      // Clicking through to the new event means user wants to follow it — switch the floor
      // map to its site/floor and drop the manual-selection block so future events auto-follow.
      setInitialSiteTag(newEvents[0]?.deviceLoc?.site?.tag);
      setInitialFloorTag(newEvents[0]?.deviceLoc?.floor?.tag);
      if (userSelectedSiteRef) userSelectedSiteRef.current = false;
    }
    setNewEventDetected(false);
    setShowNewEventBanner(false);
    isScrolledDownRef.current = false;
    // If an accordion was open, defer scroll until react-window re-renders with collapsed heights
    const scrollToTop = () => {
      if (listRef.current) {
        listRef.current.scrollToRow({ index: 0, behavior: "smooth" });
      }
    };
    if (wasExpanded) {
      setTimeout(scrollToTop, 0);
    } else {
      scrollToTop();
    }
  }

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  return (
    <ContentContainer
      isExpanded={expandedTileIndex !== null}
    >
      <DashboardEventCard
        criticalEventsCount={criticalCount}
        loading={eventsLoading}
        eventError={eventError}
        hasFetchedCriticalEventsOnce={hasFetchedCriticalEventsOnce}
        refreshHandler={() =>
          dispatch(
            fetchCriticalUnresolvedEventsRequest({
              sites: sitesList?.length ? sitesList : []
            })
          )
        }
      />
      <div style={{ position: "relative" }}>
        {(!eventsLoading && (!eventError || hasFetchedCriticalEventsOnce) && eventList?.length > 0) && (
          <List
            listRef={listRef}
            rowCount={eventList.length}
            rowHeight={getRowHeight}
            rowComponent={EventRowRenderer}
            rowProps={{
              eventList,
              expandedTileIndex,
              availableVideoEvidence,
              setAvailableVideoEvidence,
              handleTileExpand,
              setInitialSiteTag,
              setInitialFloorTag,
              onHoverChange: (isHovering) => { isHoveringRef.current = isHovering; },
            }}
            onScroll={() => {
              const el = listRef.current?.element;
              if (!el) return;
              handleScroll({ target: el });
            }}
            style={{ height: `${isMobile ? Math.min(eventList.length * COLLAPSED_ROW_HEIGHT, MOBILE_VISIBLE_ROWS * COLLAPSED_ROW_HEIGHT) : LIST_HEIGHT}px`, overflowX: "hidden", scrollbarWidth: "thin" }}
          />
        )}
        {(eventsLoading && !eventError) && <DashboardEventRowEmpty />}
        {newEventDetected && showNewEventBanner && (
          <NewEventLink>
            <TextLink id="dashboard_new-eventDetected" surface="light" onClick={handleNewEventDetected}>
              {DASHBOARD_EVENT_CARD_TEXT.NEW_EVENT_DETECTED}
            </TextLink>
          </NewEventLink>
        )}
        {pageLimitReached && (
            <NewEventLink>
              <TextLink id="dashboard_new-eventDetected" surface="light" style={{cursor:"auto"}}>
                {DASHBOARD_EVENT_CARD_TEXT.NO_MORE_EVENTS}
              </TextLink>
            </NewEventLink>
          )
        }
      </div>
      {!eventsLoading && (!eventError || hasFetchedCriticalEventsOnce) && eventList?.length > 0 && (
        <TextLinkCaretButton className="view-all-events">
          <TextLinkCaret
            id="dashboard_viewall-eventsButton"
            onClick={handleAllEventsClick}
            surface="light"
            disabled={false}
            iconPosition="right"
          >
            View all events
          </TextLinkCaret>
        </TextLinkCaretButton>
      )}
    </ContentContainer>
  );
};

export default EbcLeftContainer;
